/**
 * UPT-2079 F2 (web §5.3) — l'icône suggérée pour un élément de menu : MÊMES règles que `NavigationIcons` du serveur (vecteurs partagés
 * `nav-icon-vectors.json`, rejoués des deux côtés). Un jeton lu dans le prototype passe d'abord par `ICON_ALIASES`, puis les mots du
 * libellé par `ICON_KEYWORDS` (ordre significatif) ; aucune règle ne conclut : `null` (le Concepteur laisse alors le choix au Maker).
 * Fonctions pures ; la table des icônes admises est `NAV_ICONS` du socle.
 */
import { NAV_ICONS } from '@/uptk/core/components/common/navIcons';
import { ICON_ALIASES, ICON_KEYWORDS } from './navIconVocabulary';

const PREFIX = /^(?:lucide|fas|far|fab|fal|fad|fa|icon|bi|mdi|heroicon|ph|tabler|feather|i)-/;
const SUFFIX = /-(?:icon|outline|solid|line|fill)$/;
const HERO_KEY = /^[A-Z][A-Za-z0-9]{2,60}Icon$/;

const admitted = (key: string): boolean => Object.prototype.hasOwnProperty.call(NAV_ICONS, key);

/** Minuscules sans accents ni signes diacritiques. */
export const foldLabel = (value: string): string => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Mots d'un libellé : minuscules, sans accents, découpés sur tout ce qui n'est pas une lettre ou un chiffre. */
export const labelTokens = (label: string): string[] => foldLabel(label).split(/[^a-z0-9]+/).filter((part) => part !== '');

/** Clé d'icône pour un jeton lu dans le prototype, ou `null`. */
export const iconFromToken = (token: string | null | undefined): string | null => {
  if (!token) return null;
  const raw = token.trim();
  if (HERO_KEY.test(raw) && admitted(raw)) return raw;
  let normalized = raw.toLowerCase();
  if (normalized.startsWith('#')) normalized = normalized.slice(1);
  const stripped = normalized.replace(PREFIX, '').replace(SUFFIX, '');
  const alias = Object.prototype.hasOwnProperty.call(ICON_ALIASES, stripped) ? ICON_ALIASES[stripped] : undefined;
  return alias && admitted(alias) ? alias : null;
};

/** Clé d'icône pour un libellé (mots-clés), ou `null`. */
export const iconFromLabel = (label: string | null | undefined): string | null => {
  for (const token of labelTokens(label ?? '')) {
    for (const row of ICON_KEYWORDS) {
      for (const word of row.words) {
        const hit = word.length >= 4 ? token.startsWith(word) : token === word;
        if (hit && admitted(row.icon)) return row.icon;
      }
    }
  }
  return null;
};

/** Suggestion complète : jeton du prototype d'abord, puis mots du libellé. */
export const suggestNavIcon = (label: string | null | undefined, token?: string | null): string | null =>
  iconFromToken(token) ?? iconFromLabel(label);
